import { useState } from "react";
import { Plus, Search, FileText, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useFetchShippingCompanies } from "@/hooks/useShippingCompanies";
import { AddShippingCompanyDialog } from "@/components/shipping-companies/AddShippingCompanyDialog";
import { EditShippingCompanyDialog } from "@/components/shipping-companies/EditShippingCompanyDialog";
import type { ShippingCompany } from "@/types/shipping-company";
import * as XLSX from 'xlsx';
const ShippingCompanies = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<ShippingCompany | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const {
    data: companies = [],
    isLoading,
    refetch
  } = useFetchShippingCompanies();
  const filteredCompanies = companies.filter(company => company.name_ar.toLowerCase().includes(searchTerm.toLowerCase()) || company.name_en.toLowerCase().includes(searchTerm.toLowerCase()) || company.code.toLowerCase().includes(searchTerm.toLowerCase()));
  const handleEditClick = (company: ShippingCompany) => {
    setSelectedCompany(company);
    setShowEditDialog(true);
  };
  const handleExport = () => {
    if (filteredCompanies.length === 0) {
      toast.error("لا توجد بيانات لتصديرها");
      return;
    }
    const exportData = filteredCompanies.map(company => ({
      'الاسم بالعربية': company.name_ar,
      'الاسم بالإنجليزية': company.name_en,
      'الكود': company.code,
      'تاريخ الإنشاء': new Date(company.created_at).toLocaleDateString('ar-SA')
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "شركات النقل البحري");
    XLSX.writeFile(workbook, "shipping-companies.xlsx");
    toast.success("تم تصدير البيانات بنجاح");
  };
  if (isLoading) {
    return <div className="container mx-auto p-6" dir="rtl">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">جاري التحميل...</div>
        </div>
      </div>;
  }
  return <div className="container mx-auto p-6" dir="rtl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">شركات النقل البحري</h1>
          
        </div>
        <div className="flex gap-2">
          <Button onClick={handleExport} variant="outline" size="sm">
            <Download className="h-4 w-4 ml-2" />
            تصدير Excel
          </Button>
          <Button onClick={() => setShowAddDialog(true)} size="sm">
            <Plus className="h-4 w-4 ml-2" />
            إضافة شركة جديدة
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="flex gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="البحث في شركات النقل البحري..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pr-10" />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              إجمالي الشركات
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{companies.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              الشركات النشطة
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{companies.length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Companies Grid */}
      {filteredCompanies.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCompanies.map(company => <Card key={company.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => handleEditClick(company)}>
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{company.name_ar}</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">{company.name_en}</p>
                  </div>
                  <Badge variant="secondary">{company.code}</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">تاريخ الإضافة:</span>
                    <span className="text-sm">{new Date(company.created_at).toLocaleDateString('ar-SA')}</span>
                  </div>
                </div>
              </CardContent>
            </Card>)}
        </div> : <Card>
          <CardContent className="py-8">
            <div className="text-center">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">لا توجد شركات نقل بحري</h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm ? "لم يتم العثور على شركات مطابقة لبحثك" : "لم يتم إضافة أي شركات نقل بحري بعد"}
              </p>
              {!searchTerm && <Button onClick={() => setShowAddDialog(true)}>
                  <Plus className="h-4 w-4 ml-2" />
                  إضافة أول شركة
                </Button>}
            </div>
          </CardContent>
        </Card>}

      {/* Dialogs */}
      <AddShippingCompanyDialog open={showAddDialog} onOpenChange={setShowAddDialog} onSuccess={() => {
      refetch();
      setShowAddDialog(false);
    }} />

      {selectedCompany && <EditShippingCompanyDialog open={showEditDialog} onOpenChange={setShowEditDialog} company={selectedCompany} onSuccess={() => {
      refetch();
      setShowEditDialog(false);
      setSelectedCompany(null);
    }} />}
    </div>;
};
export default ShippingCompanies;